import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Bandage, Plus, X, Check, Edit2, Trash2, Loader2, Camera,
  AlertTriangle, TrendingDown, TrendingUp, Minus, ChevronDown,
  ChevronUp, Image, Calendar, Ruler, Activity, Eye, Sparkles
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

// =====================================================
// WOUND PHOTO CAPTURE
// =====================================================

function PhotoCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      setLoading(true);
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please ensure camera permissions are granted.');
    } finally {
      setLoading(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCaptured(dataUrl);
  };

  const retake = () => {
    setCaptured(null);
  };

  const confirmCapture = () => {
    if (captured) {
      onCapture(captured);
      stopCamera();
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="p-4 flex items-center justify-between bg-black/50">
        <h3 className="text-white font-semibold">Capture Wound Photo</h3>
        <Button variant="ghost" size="sm" onClick={() => { stopCamera(); onClose(); }} className="text-white">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="flex-1 flex items-center justify-center bg-black relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
        )}
        
        {error && (
          <div className="text-center text-white p-4">
            <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-amber-500" />
            <p>{error}</p>
            <Button onClick={startCamera} className="mt-4">Retry</Button>
          </div>
        )}

        {!captured ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="max-w-full max-h-full"
            onLoadedMetadata={() => setLoading(false)}
          />
        ) : (
          <img src={captured} alt="Captured" className="max-w-full max-h-full" />
        )}
        
        <canvas ref={canvasRef} className="hidden" />
      </div>

      <div className="p-4 bg-black/50 flex justify-center gap-4">
        {!captured ? (
          <Button
            onClick={capturePhoto}
            disabled={loading || error}
            className="w-16 h-16 rounded-full bg-white hover:bg-gray-200"
          >
            <Camera className="w-8 h-8 text-gray-900" />
          </Button>
        ) : (
          <>
            <Button onClick={retake} variant="outline" className="text-white border-white">
              Retake
            </Button>
            <Button onClick={confirmCapture} className="bg-green-600 hover:bg-green-700">
              <Check className="w-4 h-4 mr-2" /> Use Photo
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// =====================================================
// WOUND ASSESSMENT FORM
// =====================================================

function WoundAssessmentForm({ wound, existingWoundId, onSave, onCancel, options, aiPrefillData }) {
  const [formData, setFormData] = useState({
    wound_id: existingWoundId || '',
    wound_name: wound?.wound_name || '',
    body_location: aiPrefillData?.body_location || wound?.body_location || '',
    location_detail: aiPrefillData?.location_detail || wound?.location_detail || '',
    wound_type: wound?.wound_type || '',
    stage: aiPrefillData?.stage || wound?.stage || '',
    length_cm: aiPrefillData?.length_cm || wound?.length_cm || '',
    width_cm: aiPrefillData?.width_cm || wound?.width_cm || '',
    depth_cm: aiPrefillData?.depth_cm || wound?.depth_cm || '',
    undermining: wound?.undermining || '',
    tunneling: wound?.tunneling || '',
    wound_bed_tissue: wound?.wound_bed_tissue || { granulation: 0, slough: 0, eschar: 0, epithelial: 0 },
    wound_bed_description: wound?.wound_bed_description || '',
    exudate_type: wound?.exudate_type || 'none',
    exudate_amount: wound?.exudate_amount || 'none',
    exudate_odor: wound?.exudate_odor || 'none',
    periwound_condition: wound?.periwound_condition || { intact: true, macerated: false, erythema: false, induration: false },
    periwound_description: wound?.periwound_description || '',
    pain_level: wound?.pain_level ?? '',
    pain_description: wound?.pain_description || '',
    treatment_notes: wound?.treatment_notes || '',
    dressing_type: wound?.dressing_type || '',
    dressing_change_frequency: wound?.dressing_change_frequency || '',
    wound_status: wound?.wound_status || 'active'
  });
  const [loading, setLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [showAiBanner, setShowAiBanner] = useState(!!aiPrefillData);

  // Show AI prefill banner when data exists
  useEffect(() => {
    if (aiPrefillData) {
      setShowAiBanner(true);
    }
  }, [aiPrefillData]);

  const handleSubmit = async () => {
    if (!formData.body_location) {
      toast.error('Body location is required');
      return;
    }
    
    setLoading(true);
    try {
      await onSave(formData, capturedPhotos);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = (photoData) => {
    setCapturedPhotos(prev => [...prev, photoData]);
    setShowCamera(false);
    toast.success('Photo captured');
  };

  const removePhoto = (index) => {
    setCapturedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const updateTissuePercentage = (tissue, value) => {
    setFormData(prev => ({
      ...prev,
      wound_bed_tissue: { ...prev.wound_bed_tissue, [tissue]: parseInt(value) || 0 }
    }));
  };

  const togglePeriwound = (condition) => {
    setFormData(prev => ({
      ...prev,
      periwound_condition: { ...prev.periwound_condition, [condition]: !prev.periwound_condition[condition] }
    }));
  };

  return (
    <Card className="p-6 border-2 border-purple-200">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-purple-900">
          {existingWoundId ? 'New Assessment' : 'New Wound'} - {formData.wound_name || 'Wound Assessment'}
        </h4>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-6 max-h-[65vh] overflow-y-auto pr-2">
        {/* AI Prefill Banner */}
        {showAiBanner && aiPrefillData && (
          <div 
            className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3"
            data-testid="ai-wound-prefill-banner"
          >
            <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-800">AI-Extracted Wound Data</p>
              <p className="text-xs text-purple-600 mt-1">
                Location: {aiPrefillData.body_location || 'Not detected'} 
                {aiPrefillData.length_cm && ` • Dimensions: ${aiPrefillData.length_cm}×${aiPrefillData.width_cm}×${aiPrefillData.depth_cm || '?'}cm`}
                {aiPrefillData.stage && ` • Stage: ${aiPrefillData.stage}`}
              </p>
              <p className="text-xs text-gray-500 mt-1">Please verify and complete all required fields.</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowAiBanner(false)}
              className="text-purple-600 hover:bg-purple-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Wound Name</Label>
            <Input
              value={formData.wound_name}
              onChange={(e) => setFormData(prev => ({ ...prev, wound_name: e.target.value }))}
              placeholder="e.g., Left heel pressure ulcer"
            />
          </div>
          <div>
            <Label>Body Location *</Label>
            <select
              value={formData.body_location}
              onChange={(e) => setFormData(prev => ({ ...prev, body_location: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Select location...</option>
              {options?.body_locations?.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Classification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Wound Type</Label>
            <select
              value={formData.wound_type}
              onChange={(e) => setFormData(prev => ({ ...prev, wound_type: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Select type...</option>
              {options?.wound_types?.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Stage/Grade</Label>
            <select
              value={formData.stage}
              onChange={(e) => setFormData(prev => ({ ...prev, stage: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="">Select stage...</option>
              {options?.stages?.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Measurements */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <Label className="text-blue-800 mb-3 block flex items-center gap-2">
            <Ruler className="w-4 h-4" /> Measurements (cm)
          </Label>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs">Length</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.length_cm}
                onChange={(e) => setFormData(prev => ({ ...prev, length_cm: e.target.value }))}
                placeholder="L"
              />
            </div>
            <div>
              <Label className="text-xs">Width</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.width_cm}
                onChange={(e) => setFormData(prev => ({ ...prev, width_cm: e.target.value }))}
                placeholder="W"
              />
            </div>
            <div>
              <Label className="text-xs">Depth</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.depth_cm}
                onChange={(e) => setFormData(prev => ({ ...prev, depth_cm: e.target.value }))}
                placeholder="D"
              />
            </div>
          </div>
          {formData.length_cm && formData.width_cm && (
            <p className="text-xs text-blue-700 mt-2">
              Volume: {(parseFloat(formData.length_cm) * parseFloat(formData.width_cm) * (parseFloat(formData.depth_cm) || 0.1)).toFixed(2)} cm³
            </p>
          )}
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <Label className="text-xs">Undermining</Label>
              <Input
                value={formData.undermining}
                onChange={(e) => setFormData(prev => ({ ...prev, undermining: e.target.value }))}
                placeholder="e.g., 2cm at 3 o'clock"
              />
            </div>
            <div>
              <Label className="text-xs">Tunneling</Label>
              <Input
                value={formData.tunneling}
                onChange={(e) => setFormData(prev => ({ ...prev, tunneling: e.target.value }))}
                placeholder="e.g., 1.5cm at 12 o'clock"
              />
            </div>
          </div>
        </div>

        {/* Wound Bed Tissue */}
        <div className="p-4 bg-amber-50 rounded-lg">
          <Label className="text-amber-800 mb-3 block">Wound Bed Tissue (%)</Label>
          <div className="grid grid-cols-4 gap-3">
            {['granulation', 'slough', 'eschar', 'epithelial'].map(tissue => (
              <div key={tissue}>
                <Label className="text-xs capitalize">{tissue}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.wound_bed_tissue[tissue]}
                  onChange={(e) => updateTissuePercentage(tissue, e.target.value)}
                />
              </div>
            ))}
          </div>
          <Textarea
            value={formData.wound_bed_description}
            onChange={(e) => setFormData(prev => ({ ...prev, wound_bed_description: e.target.value }))}
            placeholder="Additional wound bed description..."
            rows={2}
            className="mt-2"
          />
        </div>

        {/* Exudate */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Exudate Type</Label>
            <select
              value={formData.exudate_type}
              onChange={(e) => setFormData(prev => ({ ...prev, exudate_type: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              {options?.exudate_types?.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Amount</Label>
            <select
              value={formData.exudate_amount}
              onChange={(e) => setFormData(prev => ({ ...prev, exudate_amount: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              {options?.exudate_amounts?.map(a => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Odor</Label>
            <select
              value={formData.exudate_odor}
              onChange={(e) => setFormData(prev => ({ ...prev, exudate_odor: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              <option value="none">None</option>
              <option value="faint">Faint</option>
              <option value="moderate">Moderate</option>
              <option value="strong">Strong/Foul</option>
            </select>
          </div>
        </div>

        {/* Periwound */}
        <div>
          <Label className="mb-2 block">Periwound Condition</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'intact', label: 'Intact' },
              { key: 'macerated', label: 'Macerated' },
              { key: 'erythema', label: 'Erythema' },
              { key: 'induration', label: 'Induration' }
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => togglePeriwound(key)}
                className={`px-3 py-1 rounded-full text-sm ${
                  formData.periwound_condition[key]
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Pain */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Pain Level (0-10)</Label>
            <Input
              type="number"
              min="0"
              max="10"
              value={formData.pain_level}
              onChange={(e) => setFormData(prev => ({ ...prev, pain_level: e.target.value }))}
              placeholder="0-10"
            />
          </div>
          <div>
            <Label>Pain Description</Label>
            <Input
              value={formData.pain_description}
              onChange={(e) => setFormData(prev => ({ ...prev, pain_description: e.target.value }))}
              placeholder="e.g., Sharp during dressing change"
            />
          </div>
        </div>

        {/* Treatment */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Dressing Type</Label>
            <Input
              value={formData.dressing_type}
              onChange={(e) => setFormData(prev => ({ ...prev, dressing_type: e.target.value }))}
              placeholder="e.g., Foam, Hydrocolloid"
            />
          </div>
          <div>
            <Label>Change Frequency</Label>
            <Input
              value={formData.dressing_change_frequency}
              onChange={(e) => setFormData(prev => ({ ...prev, dressing_change_frequency: e.target.value }))}
              placeholder="e.g., Every 3 days"
            />
          </div>
        </div>

        <div>
          <Label>Treatment Notes</Label>
          <Textarea
            value={formData.treatment_notes}
            onChange={(e) => setFormData(prev => ({ ...prev, treatment_notes: e.target.value }))}
            placeholder="Treatment details, observations, plan..."
            rows={3}
          />
        </div>

        {/* Status */}
        <div>
          <Label>Wound Status</Label>
          <div className="flex gap-2 mt-1">
            {['active', 'healing', 'healed', 'worsening'].map(status => (
              <button
                key={status}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, wound_status: status }))}
                className={`px-3 py-1 rounded-full text-sm capitalize ${
                  formData.wound_status === status
                    ? status === 'healed' ? 'bg-green-600 text-white' :
                      status === 'healing' ? 'bg-blue-600 text-white' :
                      status === 'worsening' ? 'bg-red-600 text-white' :
                      'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Photo Capture */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <Label className="flex items-center gap-2">
              <Camera className="w-4 h-4" /> Wound Photos
            </Label>
            <Button
              type="button"
              onClick={() => setShowCamera(true)}
              variant="outline"
              size="sm"
            >
              <Camera className="w-4 h-4 mr-2" /> Take Photo
            </Button>
          </div>
          
          {capturedPhotos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {capturedPhotos.map((photo, idx) => (
                <div key={idx} className="relative">
                  <img src={photo} alt={`Wound ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.body_location}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save Assessment
        </Button>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <PhotoCapture
          onCapture={handlePhotoCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </Card>
  );
}

// =====================================================
// HEALING TRACKER CHART
// =====================================================

function HealingTrackerChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <TrendingDown className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>No measurement data available for healing tracker</p>
      </div>
    );
  }

  const chartData = data.map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    volume: d.volume ? parseFloat(d.volume.toFixed(2)) : null,
    length: d.length,
    width: d.width
  })).filter(d => d.volume !== null);

  if (chartData.length < 2) {
    return (
      <div className="text-center text-gray-500 py-8">
        <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>Need at least 2 assessments to show healing trend</p>
      </div>
    );
  }

  // Calculate trend
  const firstVolume = chartData[0]?.volume || 0;
  const lastVolume = chartData[chartData.length - 1]?.volume || 0;
  const percentChange = firstVolume > 0 ? ((lastVolume - firstVolume) / firstVolume * 100).toFixed(1) : 0;
  const isHealing = lastVolume < firstVolume;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-700">Wound Volume Over Time</h4>
        <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
          isHealing ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {isHealing ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
          {Math.abs(percentChange)}% {isHealing ? 'decrease' : 'increase'}
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} label={{ value: 'cm³', angle: -90, position: 'insideLeft', fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} cm³`, 'Volume']}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Line 
            type="monotone" 
            dataKey="volume" 
            stroke="#8b5cf6" 
            strokeWidth={2}
            dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// =====================================================
// MAIN WOUND CARE SECTION COMPONENT
// =====================================================

export default function WoundCareSection({ clientId, aiWoundData, onAiDataUsed }) {
  const { user } = useAuth();
  const [wounds, setWounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({});
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [selectedWound, setSelectedWound] = useState(null);
  const [expandedWounds, setExpandedWounds] = useState({});
  const [healingData, setHealingData] = useState({});
  const [setupRequired, setSetupRequired] = useState(false);
  const [aiPrefillData, setAiPrefillData] = useState(null);

  const canManage = user && ['admin', 'super_admin', 'rn'].includes(user.role);

  // Handle AI wound data from voice charting
  useEffect(() => {
    if (aiWoundData?.has_wound_data && aiWoundData.wounds?.length > 0) {
      // Pre-populate with first wound from AI
      const aiWound = aiWoundData.wounds[0];
      setAiPrefillData({
        body_location: aiWound.location || '',
        location_detail: aiWound.characteristics || '',
        stage: aiWound.stage || '',
        length_cm: aiWound.length_cm || '',
        width_cm: aiWound.width_cm || '',
        depth_cm: aiWound.depth_cm || ''
      });
      setShowNewAssessment(true);
      toast.info('AI-extracted wound data pre-filled. Please review and complete the assessment.');
      // Clear AI data after using it
      onAiDataUsed && onAiDataUsed();
    }
  }, [aiWoundData]);

  const fetchWounds = async () => {
    setLoading(true);
    setSetupRequired(false);
    try {
      const [woundsRes, optionsRes] = await Promise.all([
        api.get(`/emr/wound-care/client/${clientId}`),
        api.get('/emr/wound-care/options')
      ]);
      setWounds(woundsRes.data.wounds || []);
      setOptions(optionsRes.data);
      
      // Fetch healing data for each wound
      const healingPromises = (woundsRes.data.wounds || []).map(w =>
        api.get(`/emr/wound-care/client/${clientId}/wound/${w.wound_id}`)
      );
      const healingResults = await Promise.all(healingPromises);
      const healingMap = {};
      healingResults.forEach((res, idx) => {
        healingMap[woundsRes.data.wounds[idx].wound_id] = res.data.healing_data;
      });
      setHealingData(healingMap);
      
    } catch (error) {
      console.error('Failed to fetch wounds:', error);
      if (error.response?.status === 503) {
        setSetupRequired(true);
      } else {
        toast.error('Failed to load wound assessments');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchWounds();
    }
  }, [clientId]);

  const handleSaveAssessment = async (formData, photos) => {
    try {
      // Create assessment
      const response = await api.post(`/emr/wound-care/client/${clientId}`, formData);
      const assessmentId = response.data.assessment?.id;
      const woundId = response.data.wound_id;
      
      // Upload photos if any
      for (const photoData of photos) {
        await api.post(`/emr/wound-care/client/${clientId}/photo-base64`, {
          wound_id: woundId,
          assessment_id: assessmentId,
          photo_base64: photoData
        });
      }
      
      toast.success('Wound assessment saved');
      setShowNewAssessment(false);
      setSelectedWound(null);
      fetchWounds();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save assessment');
    }
  };

  const toggleWound = (woundId) => {
    setExpandedWounds(prev => ({ ...prev, [woundId]: !prev[woundId] }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healed': return 'bg-green-100 text-green-800';
      case 'healing': return 'bg-blue-100 text-blue-800';
      case 'worsening': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'I': return 'bg-yellow-100 text-yellow-800';
      case 'II': return 'bg-orange-100 text-orange-800';
      case 'III': return 'bg-red-100 text-red-800';
      case 'IV': return 'bg-red-200 text-red-900';
      case 'unstageable': return 'bg-purple-100 text-purple-800';
      case 'DTI': return 'bg-pink-100 text-pink-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (setupRequired) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
        <h3 className="font-semibold text-gray-900 text-lg">Wound Care Setup Required</h3>
        <p className="text-gray-600 mt-2 max-w-md mx-auto">
          The Wound Care tables need to be created in your database.
        </p>
        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-left max-w-lg mx-auto">
          <p className="text-sm font-medium text-gray-700 mb-2">To enable Wound Care:</p>
          <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
            <li>Open your Supabase SQL Editor</li>
            <li>Copy the contents of <code className="bg-gray-200 px-1 rounded">/app/backend/sql/wound_care_tables.sql</code></li>
            <li>Execute the SQL script</li>
            <li>Refresh this page</li>
          </ol>
        </div>
        <Button onClick={fetchWounds} variant="outline" className="mt-4">
          <Loader2 className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="wound-care-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Bandage className="w-5 h-5 text-purple-600" />
          Wound Care
          {wounds.length > 0 && (
            <span className="text-sm font-normal text-gray-500">
              ({wounds.length} active wound{wounds.length !== 1 ? 's' : ''})
            </span>
          )}
        </h3>
        {canManage && (
          <Button
            onClick={() => { setShowNewAssessment(true); setSelectedWound(null); }}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="new-wound-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Wound Assessment
          </Button>
        )}
      </div>

      {/* New Assessment Form */}
      {showNewAssessment && (
        <WoundAssessmentForm
          wound={selectedWound?.latest_assessment}
          existingWoundId={selectedWound?.wound_id}
          options={options}
          aiPrefillData={aiPrefillData}
          onSave={(formData, photos) => {
            handleSaveAssessment(formData, photos);
            setAiPrefillData(null); // Clear prefill after save
          }}
          onCancel={() => { 
            setShowNewAssessment(false); 
            setSelectedWound(null);
            setAiPrefillData(null);
          }}
        />
      )}

      {/* Wounds List */}
      {wounds.length === 0 && !showNewAssessment ? (
        <Card className="p-8 text-center">
          <Bandage className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No wound assessments documented</p>
          {canManage && (
            <Button
              onClick={() => setShowNewAssessment(true)}
              variant="outline"
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Wound Assessment
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {wounds.map(wound => (
            <Card key={wound.wound_id} className="overflow-hidden">
              {/* Wound Header */}
              <button
                onClick={() => toggleWound(wound.wound_id)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <Bandage className="w-5 h-5 text-purple-600" />
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">{wound.wound_name}</h4>
                    <p className="text-sm text-gray-500">{wound.body_location}</p>
                  </div>
                  {wound.current_stage && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStageColor(wound.current_stage)}`}>
                      Stage {wound.current_stage}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getStatusColor(wound.current_status)}`}>
                    {wound.current_status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{wound.assessment_count} assessment{wound.assessment_count !== 1 ? 's' : ''}</span>
                  {expandedWounds[wound.wound_id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </button>

              {/* Expanded Content */}
              {expandedWounds[wound.wound_id] && (
                <div className="px-4 pb-4 border-t space-y-4">
                  {/* Healing Tracker */}
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <HealingTrackerChart data={healingData[wound.wound_id]} />
                  </div>

                  {/* Latest Assessment */}
                  {wound.latest_assessment && (
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium text-purple-900">Latest Assessment</h5>
                        <span className="text-xs text-purple-700">
                          {new Date(wound.latest_assessment.assessed_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-purple-600">Dimensions</Label>
                          <p className="font-medium">
                            {wound.latest_assessment.length_cm || '-'} × {wound.latest_assessment.width_cm || '-'} × {wound.latest_assessment.depth_cm || '-'} cm
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-purple-600">Exudate</Label>
                          <p className="font-medium capitalize">
                            {wound.latest_assessment.exudate_amount || 'None'} / {wound.latest_assessment.exudate_type || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-purple-600">Pain</Label>
                          <p className="font-medium">{wound.latest_assessment.pain_level ?? 'N/A'}/10</p>
                        </div>
                        <div>
                          <Label className="text-xs text-purple-600">Dressing</Label>
                          <p className="font-medium">{wound.latest_assessment.dressing_type || 'N/A'}</p>
                        </div>
                      </div>
                      {wound.latest_assessment.treatment_notes && (
                        <div className="mt-3">
                          <Label className="text-xs text-purple-600">Notes</Label>
                          <p className="text-sm text-purple-800">{wound.latest_assessment.treatment_notes}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  {canManage && (
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedWound(wound); setShowNewAssessment(true); }}
                      >
                        <Plus className="w-4 h-4 mr-1" /> New Assessment
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
